import { PropsWithPreview, Question, SlideComponent as SlideComponentModel } from '@/types/slide';
import { Box } from '@mui/material';
import React, { RefObject, useCallback, useEffect, useRef } from 'react';
import { useSlideComponent } from './hooks/useSlideComponent';
import { SlideComponentCharacter } from './legacy/SlideComponentCharacter/SlideComponentCharacter.legacy';
import { SlideComponentHkcsEnQ1 } from './legacy/SlideComponentHkcsEnQ1/SlideComponentHkcsEnQ1.legacy';
import { SlideComponentHkcsEnQ2 } from './legacy/SlideComponentHkcsEnQ2/SlideComponentHkcsEnQ2.legacy';
import { SlideComponentHkcsQ1 } from './legacy/SlideComponentHkcsQ1/SlideComponentHkcsQ1.legacy';
import { SlideComponentHkcsQ2 } from './legacy/SlideComponentHkcsQ2/SlideComponentHkcsQ2.legacy';
import { SlideComponentHkcsQ3 } from './legacy/SlideComponentHkcsQ3/SlideComponentHkcsQ3.legacy';
import { SlideComponentMultipleChoice } from './legacy/SlideComponentMultipleChoice.legacy';
import { SlideComponentSasAudio } from './legacy/SlideComponentSasAudio.legacy';
import { SlideComponentSasFillInTheBlanks } from './legacy/SlideComponentSasFillInTheBlanks.legacy';
import { SlideComponentSasFillInTheBlanksWriting } from './legacy/SlideComponentSasFillInTheBlanksWriting.legacy';
import { SlideComponentSasRadicalMatching } from './legacy/SlideComponentSasRadicalMatching.legacy';
import { SlideRef } from './Slide';
import { SlideComponentAudio } from './SlideComponentAudio';
import { SlideComponentAudioRecord } from './SlideComponentAudioRecord';
import { SlideComponentBackground } from './SlideComponentBackground';
import { SlideComponentCharacterCards } from './SlideComponentCharacterCards/SlideComponentCharacterCards';
import { SlideComponentCharacterWriting } from './SlideComponentCharacterWriting';
import { SlideComponentDnd } from './SlideComponentDnd/SlideComponentDnd';
import { SlideComponentDrawing } from './SlideComponentDrawing';
import { SlideComponentImage } from './SlideComponentImage';
import { SlideComponentLink } from './SlideComponentLink/SlideComponentLink';
import { SlideComponentMC } from './SlideComponentMC/SlideComponentMC';
import { SlideComponentPronunciationAssessment } from './SlideComponentPronunciationAssessment/SlideComponentPronunciationAssessment';
import { SlideComponentRadicalWriting } from './SlideComponentRadicalWriting';
import { SlideComponentSentenceMaker } from './SlideComponentSentenceMaker/SlideComponentSentenceMaker';
import { SlideComponentStrokeWriting } from './SlideComponentStrokeWriting';
import { SlideComponentText } from './SlideComponentText/SlideComponentText';

type SlideComponentProp = {
    component: SlideComponentModel;
    init?: { focus: boolean };
    slideRef: RefObject<SlideRef | null>;
    question?: Question;
};

export const SlideComponent: React.FC<PropsWithPreview<SlideComponentProp>> = ({ component, init, preview, question, slideRef, interactive }) => {
    const mainRef = useRef<HTMLDivElement | null>(null);
    const selectingRef = useRef<HTMLDivElement | null>(null);
    const { isSelected } = useSlideComponent(selectingRef, [], !!preview);

    useEffect(() => {
        if (isSelected) {
            document.dispatchEvent(
                new CustomEvent('isSelectedComponent', {
                    detail: { isSelected, componentId: component.id },
                }),
            );
        }
    }, [component.id, isSelected]);

    const RenderComponent = useCallback(() => {
        switch (component.type) {
            case 'text':
                return <SlideComponentText ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'background':
                return <SlideComponentBackground ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'image':
                return <SlideComponentImage ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'audio':
                return <SlideComponentAudio ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'dnd':
                return <SlideComponentDnd ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} question={question} />;
            case 'character':
                return <SlideComponentCharacter ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'hkcsQ1':
                return <SlideComponentHkcsQ1 ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'hkcsQ2':
                return <SlideComponentHkcsQ2 ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'hkcsQ3':
                return <SlideComponentHkcsQ3 ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'hkcsEnQ1':
                return <SlideComponentHkcsEnQ1 ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'hkcsEnQ2':
                return <SlideComponentHkcsEnQ2 ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'sasAudio':
                return <SlideComponentSasAudio ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'sasFillInTheBlanks':
                return <SlideComponentSasFillInTheBlanks ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'sasFillInTheBlanksWriting':
                return <SlideComponentSasFillInTheBlanksWriting ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'sasRadicalMatching':
                return <SlideComponentSasRadicalMatching ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'multipleChoice':
                return <SlideComponentMultipleChoice ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'characterWriting':
                return <SlideComponentCharacterWriting ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} question={question} slideRef={slideRef} />;
            case 'characterCards':
                return <SlideComponentCharacterCards ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'drawing':
                return <SlideComponentDrawing ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} question={question} slideRef={slideRef} />;
            case 'sentenceMaker':
                return <SlideComponentSentenceMaker ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'audioRecord':
                return <SlideComponentAudioRecord ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'pronunciationAssessment':
                return <SlideComponentPronunciationAssessment ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} />;
            case 'strokeWriting':
                return <SlideComponentStrokeWriting ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} question={question} slideRef={slideRef} />;
            case 'radicalWriting':
                return <SlideComponentRadicalWriting ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} question={question} slideRef={slideRef} />;
            case 'mc':
                return <SlideComponentMC ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} question={question} slideRef={slideRef} />;
            case 'link':
                return <SlideComponentLink ref={mainRef} init={{ model: component, focus: init?.focus }} preview={preview} slideRef={slideRef} interactive={interactive} />;
            default:
                console.error(`[SlideComponent] Unknown component type: ${component.type} (${component.id})`);
                return null;
        }
    }, [component, init?.focus, interactive, preview, question, slideRef]);

    return (
        <Box ref={selectingRef}>
            <RenderComponent />
        </Box>
    );
};
